import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Library, Plus, Trash2, Save, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const CATS = ["general", "cover_invite", "cover_nomination", "outreach", "recruitment", "seo"];

export const TemplatesLibrary = () => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", category: "general", subject: "", body: "" });

  const load = () => api.getTemplates().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.name.trim() || !draft.body.trim()) { toast.error("Name and body required."); return; }
    setAdding(true);
    try { await api.addTemplate(draft); setDraft({ name: "", category: "general", subject: "", body: "" }); await load(); toast.success("Template saved."); }
    catch { toast.error("Couldn't save."); }
    finally { setAdding(false); }
  };

  return (
    <div data-testid="templates-library">
      <h3 className="kz-head font-bold mb-1 flex items-center gap-2"><Library size={16} className="text-emerald-400" /> Templates library</h3>
      <p className="text-xs text-slate-500 mb-3">Every saved template, across every writing surface. Reuse, edit or delete.</p>

      <div className="space-y-1.5 mb-3" data-testid="templates-list">
        {items.length === 0 && <p className="text-xs text-slate-500">No templates yet — save one from any writing surface.</p>}
        {items.map((t) => <TemplateRow key={t.id} t={t} onChange={load} />)}
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-[#121824] p-3 space-y-2">
        <div className="flex gap-2">
          <input data-testid="tpl-new-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Template name"
            className="flex-1 bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
          <select data-testid="tpl-new-cat" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            className="bg-[#0B0F17] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-200 outline-none">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <input data-testid="tpl-new-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="Subject (optional)"
          className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
        <textarea data-testid="tpl-new-body" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={3} placeholder="Template body…"
          className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-emerald-500/40 resize-y" />
        <button data-testid="tpl-add-btn" onClick={add} disabled={adding}
          className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 disabled:opacity-60 transition-all">
          {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add template
        </button>
      </div>
    </div>
  );
};

const TemplateRow = ({ t, onChange }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({ name: t.name, category: t.category, subject: t.subject || "", body: t.body });
  const [busy, setBusy] = useState(false);

  const save = async () => { setBusy(true); try { await api.updateTemplate(t.id, d); await onChange(); toast.success("Updated."); } catch { toast.error("Update failed."); } finally { setBusy(false); } };
  const remove = async () => { setBusy(true); try { await api.deleteTemplate(t.id); await onChange(); } finally { setBusy(false); } };

  return (
    <div data-testid={`template-${t.id}`} className="rounded-lg bg-[#121824] border border-white/[0.06] px-3 py-2">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold text-slate-100 truncate">{t.name}</span>
          <span className="ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{t.category}</span>
        </div>
        <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-200">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
        <button data-testid={`del-template-${t.id}`} onClick={remove} disabled={busy} className="text-red-400/80 hover:text-red-400"><Trash2 size={13} /></button>
      </div>
      {open && (
        <div className="mt-2 space-y-1.5">
          <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500/40" />
          <input value={d.subject} onChange={(e) => setD({ ...d, subject: e.target.value })} placeholder="Subject" className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2 py-1 text-xs outline-none focus:border-emerald-500/40" />
          <textarea value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} rows={3} className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-emerald-500/40 resize-y" />
          <button data-testid={`save-template-${t.id}`} onClick={save} disabled={busy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1]"><Save size={12} /> Save</button>
        </div>
      )}
    </div>
  );
};
